"use client";

import {Button, Drawer, ScrollShadow, Separator, Typography, useOverlayState} from "@heroui/react";
import {CodeIcon} from "@project/icons-web";
import {useEffect, useState} from "react";

import {formRadiusOptions, radiusOptions} from "../../themes/builder-constants";

import {AccentColorSelector} from "./accent-color-selector";
import {BaseColorSlider} from "./base-color-slider";
import {FontFamilySelector} from "./font-family-selector";
import {RadiusPopover} from "./radius-selector";
import {RedoButton} from "./redo-button";
import {ResetButton} from "./reset-button";
import {ShuffleButton} from "./shuffle-button";
import {useThemeBuilder} from "./theme-builder-context";
import {ThemePopover} from "./theme-popover";
import {UndoButton} from "./undo-button";

/** Hook to detect if screen is below md breakpoint (768px) */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    const handleResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

/** Palette SVG icon — sized to fit the FAB naturally. */
function PaletteIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="scale-200"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10 .83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"
        fill="currentColor"
        opacity="0.25"
      />
      <circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" />
      <circle cx="9.5" cy="7.5" r="1.5" fill="currentColor" />
      <circle cx="14.5" cy="7.5" r="1.5" fill="currentColor" />
      <circle cx="17.5" cy="11.5" r="1.5" fill="currentColor" />
    </svg>
  );
}
/**
 * Mobile-optimized Theme Builder with a floating action button and a bottom sheet drawer.
 * Only renders on mobile (< 768px). Syncs open/close state with the desktop panel context.
 */
export function MobileFooter() {
  const isMobile = useIsMobile();
  const drawerState = useOverlayState();
  const {isCodePanelOpen, isOpen, params, setParam, toggleCodePanel, toggleOpen} =
    useThemeBuilder();

  // Sync context open state → drawer when switching between mobile and desktop.
  // Guard: skip sync while the code panel is open so the drawer doesn't reopen
  // automatically while the user is viewing the generated CSS.
  useEffect(() => {
    if (!isMobile) return;
    if (isCodePanelOpen) return;
    if (isOpen && !drawerState.isOpen) drawerState.open();
    else if (!isOpen && drawerState.isOpen) drawerState.close();
  }, [isMobile, isOpen, isCodePanelOpen, drawerState]);

  // Propagate drawer open/close back to context (e.g. swipe-to-dismiss)
  const handleDrawerOpenChange = (open: boolean) => {
    drawerState.setOpen(open);
    if (open !== isOpen) toggleOpen();
  };

  if (!isMobile) return null;

  return (
    <>
      {/* ── Floating Action Button ─────────────────────────────────────────── */}
      <Button
        isIconOnly
        variant="primary"
        onPress={() => {
          // Open both the local drawer state and the shared context state together.
          // If only drawerState is opened, the sync useEffect will see !isOpen && drawerState.isOpen
          // and immediately close the drawer again (feedback loop).
          drawerState.open();
          if (!isOpen) toggleOpen();
        }}
        aria-label="Open theme builder"
        className={[
          "fixed right-5 bottom-5 z-50",
          "size-14 rounded-full",
          "shadow-accent/30 shadow-xl",
          "transition-all duration-200 ease-out",
          drawerState.isOpen ? "pointer-events-none scale-75 opacity-0" : "scale-100 opacity-100",
        ].join(" ")}
      >
        <PaletteIcon />
      </Button>

      {/* ── Bottom Sheet Drawer ────────────────────────────────────────────── */}
      <Drawer.Backdrop
        isOpen={drawerState.isOpen}
        onOpenChange={handleDrawerOpenChange}
        variant="transparent"
      >
        <Drawer.Content placement="bottom">
          <Drawer.Dialog>
            <Drawer.Handle />
            <Drawer.CloseTrigger />

            <Drawer.Header>
              <Drawer.Heading>Theme Builder</Drawer.Heading>
            </Drawer.Header>

            <Drawer.Body className="flex flex-col gap-5 px-5 pb-2">
              {/* Colors ───────────────────────────────────────────────────── */}
              <section className="flex flex-col gap-3">
                <Typography className="text-muted text-[10px] font-semibold tracking-widest uppercase">
                  Colors
                </Typography>
                <div className="flex flex-col gap-4">
                  <AccentColorSelector />
                  <BaseColorSlider />
                </div>
              </section>

              <Separator variant="secondary" />

              {/* Typography & Shape — horizontally scrollable ─────────────── */}
              <section className="flex flex-col gap-3">
                <Typography className="text-muted text-[10px] font-semibold tracking-widest uppercase">
                  Typography &amp; Shape
                </Typography>
                <ScrollShadow
                  hideScrollBar
                  className="flex w-full items-start gap-4"
                  orientation="horizontal"
                >
                  <FontFamilySelector />
                  <RadiusPopover
                    description="Affects the overall UI, like menus and modals"
                    label="Radius"
                    options={radiusOptions}
                    value={params.radius}
                    onChange={(id) => setParam("radius", id)}
                    variable="radius"
                  />
                  <RadiusPopover
                    description="Affects form elements, like inputs and selects"
                    label="Radius Form"
                    options={formRadiusOptions}
                    value={params.formRadius}
                    onChange={(id) => setParam("formRadius", id)}
                    variable="formRadius"
                  />
                  <ThemePopover />
                </ScrollShadow>
              </section>
            </Drawer.Body>

            {/* Actions ──────────────────────────────────────────────────── */}
            <Drawer.Footer className="flex items-center gap-2">
              <UndoButton />
              <RedoButton />
              <ResetButton />
              <ShuffleButton />
              {/* Get CSS — closes drawer, opens code panel overlay */}
              <Button
                variant="secondary"
                size="sm"
                className="ml-auto flex items-center gap-1.5"
                onPress={() => {
                  drawerState.close();
                  toggleCodePanel();
                }}
              >
                <CodeIcon className="size-4" aria-hidden />
                Get CSS
              </Button>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </>
  );
}
